import {
    BottomPanelState,
    Notification,
    NotificationIconContainer,
    Icon,
    SecondaryCtaButton,
	NotificationArea,
} from 'slate-ui-components';
import { useTheme } from 'styled-components';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { useInterval, useUpdate } from 'react-use';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { useMediaQuery } from '@react-hook/media-query';
import { undo, VisualizerSlice } from '../store/reducers/visualizer';
import {
    NotificationButtons,
    NotificationCloseButton,
    NotificationMessage,
} from './Notifications.styled';
import { ReserveNowNotification } from './ReserveNowNotification';
import {useFrameState} from '../FrameStateProvider';

export function Notifications({panelState}: { panelState: BottomPanelState }) {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const modesReady = useAppSelector((s) => s.configuration.modesReady);
    const pendingColourPicker = useAppSelector(s => s.configuration.pendingColourPicker)
    const [showPendingColourPickerTooltip, setPendingColourPickerTooltip] = useState(false)

    const storageShowTooltip = useAppSelector(
        (s) => s.configuration.showTooltip
    );
    const storageSavedBuild = useAppSelector((s) => s.configuration.saved);
    const [showSavedMessage, setShowSavedMessage] = useState(0);
    const [showTooltip, setShowTooltip] = useState(false);
    const theme = useTheme();
    const isFullScreenLayout = useMediaQuery(
        theme.breakpoints.fullScreenStream
    );
    const { isFullscreen } = useFrameState();
    const [showLandscapeWarning, setShowLandscapeWarning] = useState(false);

    const idleTimer = useAppSelector((s) => s.configuration.idleTimeout);
    const idleTimerEpoch = useAppSelector(
        (s) => s.configuration.idleTimeoutEpoch
    );
    const undoMessage = useAppSelector((s) => s.configuration.undoMessage);

    useEffect(() => {
        if (!isFullscreen && isFullScreenLayout) {
            setShowLandscapeWarning(true);
            setTimeout(() => setShowLandscapeWarning(false), 5000);
        }
    }, [isFullscreen, isFullScreenLayout, showLandscapeWarning]);

    useEffect(() => {
        if (storageShowTooltip && !showTooltip) {
            setShowTooltip(true);
            setTimeout(() => setShowTooltip(false), 5000);
        }
    }, [storageShowTooltip, setShowTooltip, showTooltip]);

    useEffect(() => {
        if(pendingColourPicker) {
            setPendingColourPickerTooltip(true)
            setTimeout(() => setPendingColourPickerTooltip(false), 4000)
        }
        else {
            setPendingColourPickerTooltip(false)
        }
    }, [pendingColourPicker])

    useEffect(() => {
        if (!storageSavedBuild) {
            setShowSavedMessage(0);
        } else if (!showSavedMessage) {
            setShowSavedMessage(1);
            setTimeout(() => setShowSavedMessage(2), 2500);
        }
    }, [showSavedMessage, storageSavedBuild]);

    const update = useUpdate();
    useInterval(update, 1000);
    const idleTimeLeft = idleTimer
        ? Math.max(
              0,
              Math.ceil((idleTimerEpoch! + idleTimer - Date.now()) / 1000)
          )
        : 0;

    function formatValues(input: Record<string, MessageDescriptor>) {
        return Object.fromEntries(
            Object.entries(input).map(([k, v]) => [k, intl.formatMessage(v)])
        );
    }

	const show3DEnabledNotification = showTooltip && modesReady['3D'];
	const show3DIdleNotification = idleTimeLeft > 0 && modesReady['3D'];

    return (
		<NotificationArea $notificationsOffset={panelState == "collapsed" ? 0 : 10} $showBorder={show3DEnabledNotification}>
            <Notification
                $mode="primary"
                $show={show3DEnabledNotification}
                onClick={() => {
                    setShowTooltip(false);
                }}>
                <NotificationIconContainer>
                    <Icon icon="HandMove" width={'35px'} />
                </NotificationIconContainer>
                <NotificationMessage>
                    <span>
                        <FormattedMessage
                            id="configurator.3DMode.tooltip"
                            /* eslint-disable-next-line formatjs/enforce-placeholders */
                            defaultMessage="<b>3D mode ON!</b> You can now look around and zoom in."
                        />
                    </span>
                </NotificationMessage>
            </Notification>
            <Notification
                $mode="secondary"
                $show={show3DIdleNotification}>
                <NotificationIconContainer>
                    <Icon icon="Hourglass" />
                </NotificationIconContainer>
                <NotificationMessage>
                    <span>
                        <FormattedMessage
                            id="configurator.3DMode.idleMessage"
                            /* eslint-disable-next-line formatjs/enforce-placeholders */
                            defaultMessage="<b>Still there?</b>"
                            /* values={{s: idleTimeLeft ?? 0}}*/
                        />
                    </span>
                </NotificationMessage>
            </Notification>

            <Notification
                $mode="primary"
                $show={showPendingColourPickerTooltip}
                onClick={() => {
                    setPendingColourPickerTooltip(false);
                }}>
                <NotificationIconContainer>
                    <Icon icon="Hourglass"/>
                </NotificationIconContainer>
                <NotificationMessage>
                    <span>
                        <FormattedMessage
                            id="configurator.pendingColourPicker.tooltip"
                            /* eslint-disable-next-line formatjs/enforce-placeholders */
                            defaultMessage="<b>Preparing colour picker.</b> You can continue to personalize while you wait."
                        />
                    </span>
                </NotificationMessage>
            </Notification>

            <Notification
                $mode="secondary"
                $show={showLandscapeWarning && isFullScreenLayout}>
                <NotificationIconContainer>
                    <Icon icon="Restart" />
                </NotificationIconContainer>
                <NotificationMessage>
                    <span>
                        <FormattedMessage
                            id="configurator.landscape.warning"
                            /* eslint-disable-next-line formatjs/enforce-placeholders */
                            defaultMessage="<b>Turn your device</b> to see more options"
                        />
                    </span>
                </NotificationMessage>
            </Notification>

            <Notification $mode="secondary" $show={showSavedMessage === 1}>
                <NotificationIconContainer>
                    <Icon icon="HeartFilled" />
                </NotificationIconContainer>
                <NotificationMessage>
                    <span>
                        <FormattedMessage
                            id="configurator.savedBuild"
                            defaultMessage="Saved Build"
                        />
                    </span>
                </NotificationMessage>
            </Notification>

            <ReserveNowNotification canShow={true} />

            {panelState != 'collapsed' && (
                <Notification
                    $mode="secondary"
                    $show={!!undoMessage}
                    onClick={() =>
                        dispatch(VisualizerSlice.actions.dismissUndo())
                    }>
                    <NotificationIconContainer></NotificationIconContainer>
                    <NotificationMessage>
                        {undoMessage && (
                            <span>
                                <FormattedMessage
                                    {...undoMessage.message}
                                    values={formatValues(undoMessage.values)}
                                />
                            </span>
                        )}
                    </NotificationMessage>
                    <NotificationButtons>
                        <SecondaryCtaButton
                            label={intl.formatMessage({
                                id: 'general.undo',
                                defaultMessage: 'Undo',
                            })}
                            onClick={() => dispatch(undo())}>
                            {intl.formatMessage({
                                id: 'general.undo',
                                defaultMessage: 'Undo',
                            })}
                        </SecondaryCtaButton>
                        <NotificationCloseButton
                            label={intl.formatMessage({
                                id: 'general.close',
                                defaultMessage: 'Close',
                            })}>
                            <Icon icon="Close" />
                        </NotificationCloseButton>
                    </NotificationButtons>
                </Notification>
            )}
        </NotificationArea>
    );
}
